import { createReducer } from '@reduxjs/toolkit';
import { graphql, readInlineData } from 'react-relay';
import { getOfferCurrency } from '../helpers/checkoutHelpers';
import { wasPaymentDenied } from '../helpers/checkoutForms';
import { getGuestUserId } from '../helpers/checkoutGuestUserToken';
import {
    checkoutErrorShownAction,
    closeHeaderBannerAction,
    finishAddressSaveAction,
    setHeaderBannerAction,
    setQuantityErrorAction,
    setWasNavigatedDueTaxWarningAction,
    startAddressSaveAction,
    startCheckoutProcessingAction,
    toggleAcknowledgedRestrictionsAction,
    toggleMarketingOptInAction,
    transactionSetupAction,
    updateSidemarkStatusAction,
    updateDeliveryOptions,
    setExitCheckoutAction,
    setAuthState,
    deliveryOptionsComputing,
    setTriggeredErrors,
    setCheckoutHelpType,
    type CheckoutHelpType,
    setCheckoutHelp,
    type CheckoutHelp,
    setPreRenderState,
    toggleShippingNotesError,
    toggleProcessingOffer,
} from '../actions/checkoutUIActions';
import {
    registrationErrorAction,
    setProcessingErrorAction,
    shippingCompletedAction,
    transactionBuyerChangedAction,
    setUserMismatchErrorAction,
} from '../actions/commonActions';
import { ERRORS, CHECKOUT_TRACKED_ERRORS_TRIGGER_COUNT } from '../constants/checkoutConstants';
import { Banner } from '../shared/banners';
import {
    PrerenderedCheckoutRootState,
    PRE_RENDER_STATE_INITIAL,
} from '../prerender/prerenderedCheckoutStore';

import { checkoutUIReducer_viewer$key } from './__generated__/checkoutUIReducer_viewer.graphql';
import { checkoutUIReducer_buyer$key } from './__generated__/checkoutUIReducer_buyer.graphql';
import { checkoutUIReducer_transaction$key } from './__generated__/checkoutUIReducer_transaction.graphql';

type UIState = {
    canSelectChargeCurrency: boolean;
    customerAcknowledgedRestrictions: boolean;
    hasSetUpCheckout: boolean;
    hasShownGlobalProcessingError: boolean;
    hasUnsavedSidemark: boolean;
    hasQuantityError: boolean;
    isReadyAfterItemBuyerFetch: boolean;
    isProcessing: boolean;
    isProcessingOffer: boolean;
    isMarketingOptIn: boolean;
    isCheckoutBlocked: boolean;
    isSavingAddress: boolean;
    banner: Banner | null;
    hasPassedVerification: boolean;
    wasNavigatedDueTaxWarning: boolean;
    errorCode: (typeof ERRORS)[keyof typeof ERRORS] | null;
    globalProcessingError: unknown;
    globalUserMismatchError: unknown;
    allowExitCheckout: boolean;
    authState: 'IDLE' | 'LOADING' | 'CHECKED';
    deliveryOptionsComputing: boolean;
    triggeredErrors: Record<string, number>;
    trackTriggeredErrors: boolean;
    checkoutHelpType: CheckoutHelpType | null;
    checkoutHelp: CheckoutHelp;
    preRenderState: PrerenderedCheckoutRootState;
    hasShippingNotesError: boolean;
};
const initialState: UIState = {
    canSelectChargeCurrency: false,
    customerAcknowledgedRestrictions: false,
    hasSetUpCheckout: false,
    hasShownGlobalProcessingError: false,
    hasUnsavedSidemark: false,
    hasQuantityError: false,
    isReadyAfterItemBuyerFetch: false,
    isProcessing: false,
    isProcessingOffer: false,
    isMarketingOptIn: false,
    isCheckoutBlocked: false,
    isSavingAddress: false,
    banner: null,
    hasPassedVerification: false,
    wasNavigatedDueTaxWarning: false,
    errorCode: null,
    globalProcessingError: null,
    globalUserMismatchError: null,
    allowExitCheckout: false,
    authState: 'IDLE',
    deliveryOptionsComputing: false,
    triggeredErrors: {},
    trackTriggeredErrors: true,
    checkoutHelpType: null,
    checkoutHelp: {
        showCheckoutHelp: false,
        placement: 'checkout',
    },
    preRenderState: PRE_RENDER_STATE_INITIAL,
    hasShippingNotesError: false,
};

export const checkoutUIReducer = createReducer(initialState, builder => {
    builder
        .addCase(transactionSetupAction, (state, action) => {
            const {
                viewer: viewerRef,
                buyer: buyerRef,
                transaction: transactionRef,
            } = action.payload;
            const viewer = readInlineData<checkoutUIReducer_viewer$key>(
                graphql`
                    fragment checkoutUIReducer_viewer on Viewer @inline {
                        chargeCurrencies
                    }
                `,
                viewerRef
            );
            const buyer = readInlineData<checkoutUIReducer_buyer$key>(
                graphql`
                    fragment checkoutUIReducer_buyer on User @inline {
                        hasSupportedChargeCurrency
                        preferences {
                            currency
                        }
                    }
                `,
                buyerRef
            );

            const transaction = readInlineData<checkoutUIReducer_transaction$key>(
                graphql`
                    fragment checkoutUIReducer_transaction on Transaction @inline {
                        warnings(countryCode: $userCountryCode) {
                            isCheckoutBlocked
                        }
                    }
                `,
                transactionRef
            );
            const { chargeCurrencies = [] } = viewer;
            const { hasSupportedChargeCurrency, preferences } = buyer || {};
            const isCheckoutBlocked = transaction?.warnings?.isCheckoutBlocked;
            const offerCurrency = getOfferCurrency(preferences?.currency);
            const canSelectChargeCurrency =
                buyer && !getGuestUserId()
                    ? !hasSupportedChargeCurrency
                    : !chargeCurrencies?.includes(offerCurrency);

            state.hasSetUpCheckout = true;
            state.isCheckoutBlocked = !!isCheckoutBlocked;
            state.isMarketingOptIn = state.isMarketingOptIn;
            state.canSelectChargeCurrency = canSelectChargeCurrency;
        })
        .addCase(transactionBuyerChangedAction, state => {
            state.hasSetUpCheckout = false;
            state.isReadyAfterItemBuyerFetch = false;
            state.isSavingAddress = false;
            state.errorCode = null;
        })
        .addCase(deliveryOptionsComputing, state => {
            state.deliveryOptionsComputing = true;
        })
        .addCase(updateDeliveryOptions, state => {
            state.isReadyAfterItemBuyerFetch = true;
            state.deliveryOptionsComputing = false;
        })
        .addCase(toggleMarketingOptInAction, state => {
            state.isMarketingOptIn = !state.isMarketingOptIn;
        })
        .addCase(shippingCompletedAction, (state, action) => {
            const { isCheckoutBlocked } = action.payload;
            state.isCheckoutBlocked = isCheckoutBlocked;
            state.customerAcknowledgedRestrictions = false;
        })
        .addCase(setQuantityErrorAction, (state, action) => {
            const { hasQuantityError } = action.payload;
            state.hasQuantityError = hasQuantityError;
        })
        .addCase(updateSidemarkStatusAction, (state, action) => {
            const { hasUnsavedSidemark } = action.payload;
            state.hasUnsavedSidemark = hasUnsavedSidemark;
        })
        .addCase(toggleAcknowledgedRestrictionsAction, state => {
            state.customerAcknowledgedRestrictions = !state.customerAcknowledgedRestrictions;
        })
        .addCase(checkoutErrorShownAction, state => {
            state.hasShownGlobalProcessingError = true;
        })
        .addCase(startCheckoutProcessingAction, state => {
            state.isProcessing = true;
            state.globalProcessingError = null;
        })
        .addCase(setProcessingErrorAction, (state, action) => {
            const { errorStatus, errorMessage } = action.payload;
            const { globalProcessingError, hasShownGlobalProcessingError } = state;
            const isDenied = wasPaymentDenied({ errorStatus, errorMessage });
            state.isProcessing = false;
            state.hasShownGlobalProcessingError = isDenied ? hasShownGlobalProcessingError : false;
            state.globalProcessingError = isDenied ? globalProcessingError : errorStatus;
        })
        .addCase(setUserMismatchErrorAction, (state, action) => {
            const { userMismatch } = action.payload;
            if (userMismatch) {
                state.globalUserMismatchError = ERRORS.USER_MISMATCH;
            } else {
                state.globalUserMismatchError = null;
            }
        })
        .addCase(startAddressSaveAction, state => {
            state.isSavingAddress = true;
        })
        .addCase(finishAddressSaveAction, state => {
            state.isSavingAddress = false;
        })
        .addCase(registrationErrorAction, state => {
            state.isSavingAddress = false;
        })
        .addCase(closeHeaderBannerAction, state => {
            state.banner = null;
        })
        .addCase(setHeaderBannerAction, (state, action) => {
            const { banner } = action.payload;
            state.banner = banner;
        })
        .addCase(setWasNavigatedDueTaxWarningAction, (state, action) => {
            const wasNavigatedDueTaxWarning = action.payload;
            state.wasNavigatedDueTaxWarning = wasNavigatedDueTaxWarning;
        })
        .addCase(setExitCheckoutAction, (state, action) => {
            state.allowExitCheckout = action.payload;
        })
        .addCase(setAuthState, (state, action) => {
            state.authState = action.payload;
        })
        .addCase(setTriggeredErrors, (state, action) => {
            if (state.trackTriggeredErrors) {
                const newTriggeredErrors = { ...state.triggeredErrors };
                action.payload.forEach(error => {
                    newTriggeredErrors[error] = newTriggeredErrors[error] + 1 || 1;
                });
                state.triggeredErrors = newTriggeredErrors;

                state.trackTriggeredErrors = !Object.values(newTriggeredErrors).some(
                    count => count >= CHECKOUT_TRACKED_ERRORS_TRIGGER_COUNT
                );
            }
        })
        .addCase(setCheckoutHelpType, (state, action) => {
            state.checkoutHelpType = action.payload;
        })
        .addCase(setCheckoutHelp, (state, action) => {
            const { showCheckoutHelp, placement, step } = action.payload;
            state.checkoutHelp = {
                showCheckoutHelp,
                placement: placement || 'checkout',
                step,
            };
        })
        .addCase(setPreRenderState, (state, action) => {
            state.preRenderState = action.payload;
        })
        .addCase(toggleShippingNotesError, (state, action) => {
            state.hasShippingNotesError = action.payload;
        })
        .addCase(toggleProcessingOffer, (state, action) => {
            state.isProcessingOffer = action.payload;
        });
});
